/**
 * Object Sync Utilities
 * Utilities for syncing library objects with their template versions
 */

import { GlossaryBlueprint } from '@/types-games';
import { ObjectItem, sectionDataObject } from '@/lib/stores/objectsStore';
import { extractBlueprintVersion, versionsMatch } from './versionUtils';
import { rebuildGlossaryItemFromBlueprint } from './objectParser';

/**
 * Represents a single field change detected during sync
 */
export interface FieldChange {
  fieldId: string;
  fieldLabel: string;
  sectionId: string;
  sectionTitle: string;
  changeType: 'added' | 'removed' | 'type_changed';
  oldType?: string;
  newType?: string;
  currentValue?: any;
}

/**
 * Summary of all changes for a single object
 */
export interface ObjectChangeSummary {
  object: ObjectItem;
  addedFields: FieldChange[];
  removedFields: FieldChange[];
  typeChangedFields: FieldChange[];
  hasChanges: boolean;
}

/**
 * Decision for how to handle a type conflict
 * For types→text conversions, additional options are available:
 * - 'convert_id': Use the element ID as the text value
 * - 'convert_name': Look up and use the element name as the text value
 */
export interface TypeConflictDecision {
  fieldId: string;
  action: 'clear' | 'convert' | 'keep' | 'convert_id' | 'convert_name';
}

/**
 * Extract field information from blueprint sections
 */
function extractBlueprintFields(blueprint: GlossaryBlueprint): Map<string, { type: string; label: string; sectionId: string; sectionTitle: string }> {
  const fields = new Map<string, { type: string; label: string; sectionId: string; sectionTitle: string }>();
  
  if (!blueprint.data || typeof blueprint.data !== 'object') {
    return fields;
  }
  
  const blueprintData = blueprint.data as Record<string, any>;
  const blueprintSections = blueprintData.sections;
  
  if (!blueprintSections || typeof blueprintSections !== 'object') {
    return fields;
  }
  
  Object.entries(blueprintSections).forEach(([sectionKey, sectionConfig]) => {
    if (typeof sectionConfig !== 'object') return;
    
    const sectionTitle = (sectionConfig as any)._sectionTitle || sectionKey;
    
    // Check if this is a single field section (has direct type property)
    if ((sectionConfig as any).type) {
      const fieldConfig = sectionConfig as any;
      const fieldId = fieldConfig.id || `${sectionKey}_field`;
      fields.set(fieldId, {
        type: fieldConfig.type,
        label: fieldConfig.label || sectionTitle,
        sectionId: sectionKey,
        sectionTitle,
      });
    } else {
      // Multi-field section
      Object.entries(sectionConfig as Record<string, any>).forEach(([fieldKey, fieldConfig]) => {
        // Skip metadata properties
        if (fieldKey.startsWith('_')) return;
        
        if (typeof fieldConfig === 'object' && fieldConfig.type) {
          const fieldId = fieldConfig.id || `${sectionKey}_${fieldKey}`;
          fields.set(fieldId, {
            type: fieldConfig.type,
            label: fieldConfig.label || fieldKey,
            sectionId: sectionKey,
            sectionTitle,
          });
        }
      });
    }
  });
  
  return fields;
}

/**
 * Extract field information from object data
 */
function extractObjectFields(object: ObjectItem): Map<string, { type: string; label: string; sectionId: string; sectionTitle: string; value: any }> {
  const fields = new Map<string, { type: string; label: string; sectionId: string; sectionTitle: string; value: any }>();
  
  if (!object.data || !Array.isArray(object.data)) {
    return fields;
  }
  
  object.data.forEach((section: sectionDataObject) => {
    if (!section.fields || !Array.isArray(section.fields)) return;
    
    section.fields.forEach((field) => {
      fields.set(field.id, {
        type: field.type,
        label: field.label,
        sectionId: section.id,
        sectionTitle: section.title,
        value: field.value,
      });
    });
  });
  
  return fields;
}

/**
 * Detect all field changes between an object and its blueprint
 * Returns categorized changes: added fields, removed fields, and type-changed fields
 */
export function detectFieldChanges(
  object: ObjectItem,
  blueprint: GlossaryBlueprint
): ObjectChangeSummary {
  const blueprintFields = extractBlueprintFields(blueprint);
  const objectFields = extractObjectFields(object);
  
  const addedFields: FieldChange[] = [];
  const removedFields: FieldChange[] = [];
  const typeChangedFields: FieldChange[] = [];
  
  // Find added fields (in blueprint but not in object)
  blueprintFields.forEach((bpField, fieldId) => {
    if (!objectFields.has(fieldId)) {
      addedFields.push({
        fieldId,
        fieldLabel: bpField.label,
        sectionId: bpField.sectionId,
        sectionTitle: bpField.sectionTitle,
        changeType: 'added',
        newType: bpField.type,
      });
    }
  });
  
  // Find removed fields and type changes (in object but check against blueprint)
  objectFields.forEach((objField, fieldId) => {
    const bpField = blueprintFields.get(fieldId);
    
    if (!bpField) {
      // Field was removed from blueprint
      removedFields.push({
        fieldId,
        fieldLabel: objField.label,
        sectionId: objField.sectionId,
        sectionTitle: objField.sectionTitle,
        changeType: 'removed',
        oldType: objField.type,
        currentValue: objField.value,
      });
    } else if (bpField.type !== objField.type) {
      // Field type changed
      typeChangedFields.push({
        fieldId,
        fieldLabel: objField.label,
        sectionId: objField.sectionId,
        sectionTitle: objField.sectionTitle,
        changeType: 'type_changed',
        oldType: objField.type,
        newType: bpField.type,
        currentValue: objField.value,
      });
    }
  });
  
  return {
    object,
    addedFields,
    removedFields,
    typeChangedFields,
    hasChanges: addedFields.length > 0 || removedFields.length > 0 || typeChangedFields.length > 0,
  };
}

/**
 * Detect field changes for multiple objects
 */
export function detectFieldChangesForObjects(
  objects: ObjectItem[],
  blueprint: GlossaryBlueprint
): ObjectChangeSummary[] {
  return objects.map(obj => detectFieldChanges(obj, blueprint));
}

/**
 * Get aggregated type conflicts across all objects
 * Returns unique field conflicts (by fieldId) with count of affected objects
 */
export function getAggregatedTypeConflicts(
  changeSummaries: ObjectChangeSummary[]
): Array<FieldChange & { affectedObjectCount: number }> {
  const conflictMap = new Map<string, FieldChange & { affectedObjectCount: number }>();
  
  changeSummaries.forEach(summary => {
    summary.typeChangedFields.forEach(conflict => {
      const existing = conflictMap.get(conflict.fieldId);
      if (existing) {
        existing.affectedObjectCount++;
      } else {
        conflictMap.set(conflict.fieldId, {
          ...conflict,
          affectedObjectCount: 1,
        });
      }
    });
  });
  
  return Array.from(conflictMap.values());
}

/**
 * Get all objects that are out of sync with a specific template
 */
export function getOutOfSyncObjects(
  blueprint: GlossaryBlueprint,
  allObjects: ObjectItem[]
): ObjectItem[] {
  if (!blueprint.id || !blueprint.version) return [];
  
  const blueprintVersion = extractBlueprintVersion(blueprint);
  
  return allObjects.filter(obj => {
    // Only check objects that belong to this blueprint
    if (obj.blueprint_id !== blueprint.id) return false;
    if (obj.blueprint_version === undefined) return false;
    
    // Convert item's blueprint version to comparable number
    let itemVersion: number | undefined;
    if (typeof obj.blueprint_version === 'number') {
      itemVersion = obj.blueprint_version;
    } else if (obj.blueprint_version && typeof obj.blueprint_version === 'object' && 'major' in obj.blueprint_version) {
      itemVersion = obj.blueprint_version.major * 1000000 + obj.blueprint_version.minor * 1000 + obj.blueprint_version.patch;
    }
    
    // Check if versions differ
    return !versionsMatch(blueprintVersion, itemVersion);
  });
}

/**
 * Attempt to convert a value from one type to another
 */
function attemptTypeConversion(value: any, fromType: string, toType: string): any {
  if (value === undefined || value === null || value === '') {
    return getDefaultForType(toType);
  }
  
  // String to number
  if (fromType === 'text' && toType === 'number') {
    const num = parseFloat(String(value));
    return isNaN(num) ? 0 : num;
  }
  
  // Number to string
  if (fromType === 'number' && (toType === 'text' || toType === 'textarea')) {
    return String(value);
  }
  
  // Boolean to string
  if (fromType === 'boolean' && (toType === 'text' || toType === 'textarea')) {
    return value ? 'true' : 'false';
  }
  
  // String to boolean
  if ((fromType === 'text' || fromType === 'textarea') && toType === 'boolean') {
    const str = String(value).toLowerCase().trim();
    return str === 'true' || str === '1' || str === 'yes';
  }
  
  // Single to multiple (wrap in array)
  if (!fromType.includes('multiple') && toType.includes('multiple')) {
    return value ? [value] : [];
  }
  
  // Multiple to single (take first)
  if (fromType.includes('multiple') && !toType.includes('multiple')) {
    return Array.isArray(value) && value.length > 0 ? value[0] : getDefaultForType(toType);
  }
  
  // Textarea to text (just keep the text)
  if (fromType === 'textarea' && toType === 'text') {
    return String(value);
  }
  
  // Text to textarea
  if (fromType === 'text' && toType === 'textarea') {
    return String(value);
  }
  
  // For other conversions, return the default for the new type
  return getDefaultForType(toType);
}

/**
 * Get default value for a field type
 */
function getDefaultForType(type: string): any {
  switch (type) {
    case 'boolean':
      return false;
    case 'number':
      return 0;
    case 'tags':
    case 'types':
      return [];
    default:
      return '';
  }
}

/**
 * Type element lookup function type
 * Used to look up element names by ID when converting types→text
 */
export type TypeElementLookup = (elementId: string) => { id: string; name: string } | undefined;

/**
 * Rebuild an object to match the current blueprint version
 * Preserves existing field values where field IDs match
 * @param obj - The object to sync
 * @param blueprint - The blueprint to sync to
 * @param typeDecisions - Optional decisions for how to handle type conflicts
 * @param newStatus - Optional new status to set on the object
 * @param typeElementLookup - Optional function to look up type element names
 */
export function syncObjectToBlueprint(
  obj: ObjectItem,
  blueprint: GlossaryBlueprint,
  typeDecisions?: TypeConflictDecision[],
  newStatus?: string,
  typeElementLookup?: TypeElementLookup
): ObjectItem {
  // Validate that the object has required fields
  if (!obj.game_id) {
    console.error('[syncObjectToBlueprint] Object is missing game_id:', obj.id, obj);
    throw new Error(`Cannot sync object to blueprint: missing game_id. Object ID: ${obj.id}`);
  }
  
  // Rebuild the object structure from the blueprint
  const { sections } = rebuildGlossaryItemFromBlueprint(blueprint, obj);
  
  // Apply type conflict decisions if provided
  if (typeDecisions && typeDecisions.length > 0) {
    const decisionsMap = new Map(typeDecisions.map(d => [d.fieldId, d.action]));
    const objectFields = extractObjectFields(obj);
    const blueprintFields = extractBlueprintFields(blueprint);
    
    // Process each section and field
    sections.forEach(section => {
      section.fields.forEach(field => {
        const decision = decisionsMap.get(field.id);
        if (decision) {
          const oldFieldInfo = objectFields.get(field.id);
          const newFieldInfo = blueprintFields.get(field.id);
          
          if (oldFieldInfo && newFieldInfo && oldFieldInfo.type !== newFieldInfo.type) {
            switch (decision) {
              case 'clear':
                // Use default value for new type
                field.value = getDefaultForType(newFieldInfo.type);
                break;
              case 'convert':
                // Attempt to convert the value
                field.value = attemptTypeConversion(
                  oldFieldInfo.value,
                  oldFieldInfo.type,
                  newFieldInfo.type
                );
                break;
              case 'convert_id':
                // Use the element ID as text (for types→text conversion)
                if (oldFieldInfo.type === 'types' && isTextType(newFieldInfo.type)) {
                  field.value = String(oldFieldInfo.value || '');
                } else {
                  field.value = attemptTypeConversion(oldFieldInfo.value, oldFieldInfo.type, newFieldInfo.type);
                }
                break;
              case 'convert_name':
                // Look up and use the element name (for types→text conversion)
                if (oldFieldInfo.type === 'types' && isTextType(newFieldInfo.type) && typeElementLookup) {
                  const elementId = String(oldFieldInfo.value || '');
                  const element = typeElementLookup(elementId);
                  field.value = element?.name || elementId;
                } else {
                  field.value = attemptTypeConversion(oldFieldInfo.value, oldFieldInfo.type, newFieldInfo.type);
                }
                break;
              case 'keep':
                // Keep the original value (may cause issues but user chose this)
                field.value = oldFieldInfo.value;
                break;
            }
          }
        }
      });
    });
  }
  
  // Get the current blueprint version
  const blueprintVersion = extractBlueprintVersion(blueprint);
  
  // Return the updated object with new structure and version
  return {
    ...obj,
    data: sections,
    blueprint_version: blueprintVersion,
    ...(newStatus !== undefined ? { status: newStatus } : {}),
  };
}

/**
 * Check if a type is a text-based type
 */
function isTextType(type: string): boolean {
  return type === 'text' || type === 'title' || type === 'textarea';
}

/**
 * Check if a conversion is from types to text
 */
export function isTypesToTextConversion(oldType: string, newType: string): boolean {
  return oldType === 'types' && isTextType(newType);
}

/**
 * Sync multiple objects to their blueprint
 */
export function syncObjectsToBlueprint(
  objects: ObjectItem[],
  blueprint: GlossaryBlueprint
): ObjectItem[] {
  return objects.map(obj => syncObjectToBlueprint(obj, blueprint));
}
